// Single source of truth for cap table data.
// Both the full CapTable component (Equity tab) and the investor
// OwnershipCard MUST import from here so the two views never diverge.
//
// NOTE: All real cap-table data has been removed. These exports are
// intentionally empty placeholders. Export names and shapes are preserved
// so dependent imports keep working, but they carry no meaningful data.
// When a real data source (e.g. Supabase `cap_table`) is wired, populate
// these from there.

const STORAGE_KEY = 'finkloz_captable_edits_v1';
const CHANGE_EVENT = 'finkloz:captable-changed';

// Proactively clear any previously cached cap-table edits so no real data
// lingers in localStorage from earlier sessions.
try {
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(STORAGE_KEY);
  }
} catch {
  /* ignore storage access errors */
}

export function saveCapTableEdits({ rounds, shareholders, optionPool }) {
  try {
    const payload = {
      updatedAt: new Date().toISOString(),
      rounds, shareholders, optionPool,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    // Notify any listening components in the same tab
    window.dispatchEvent(new Event(CHANGE_EVENT));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('saveCapTableEdits failed', e);
  }
}

export function loadCapTableEdits() {
  // Intentionally returns null: do not hydrate any cached cap-table edits.
  return null;
}

export function subscribeToCapTableChanges(callback) {
  const handler = () => callback();
  window.addEventListener(CHANGE_EVENT, handler);
  window.addEventListener('storage', handler);
  return () => {
    window.removeEventListener(CHANGE_EVENT, handler);
    window.removeEventListener('storage', handler);
  };
}

// Empty placeholders — no real rounds, shareholders, or option pool.
export const CAPTABLE_ROUNDS = [];

export const CAPTABLE_SHAREHOLDERS = [];

export const CAPTABLE_OPTION_POOL = { total: 0, outstanding: 0, available: 0 };

// Return the current (possibly edited) shareholders, rounds, and option pool.
// With no cached edits, this returns the empty baselines.
export function getCurrentCapTable() {
  const edits = loadCapTableEdits();
  return {
    rounds: edits?.rounds || CAPTABLE_ROUNDS,
    shareholders: edits?.shareholders || CAPTABLE_SHAREHOLDERS,
    optionPool: edits?.optionPool || CAPTABLE_OPTION_POOL,
    updatedAt: edits?.updatedAt || null,
  };
}

// Derive a compact ownership summary matching what the Equity / Cap Table tab shows.
// With no data, every figure is zero and the breakdown is empty.
export function getOwnershipSummary() {
  const { shareholders, rounds, optionPool, updatedAt } = getCurrentCapTable();
  const buckets = {
    Founders: 0,  // common held by founders + purchased options
    Seed: 0,      // preseed + seed
    'Series A': 0, // serA1 + serA2
    'Series A.2': 0, // serA22
    'Option Pool': optionPool.total,
  };

  let canadianShares = 0;
  let totalExcludingPool = 0;

  for (const sh of shareholders) {
    const h = sh.h || {};
    const common = h.common || 0;
    const preseed = h.preseed || 0;
    const seed = h.seed || 0;
    const serA1 = h.serA1 || 0;
    const serA2 = h.serA2 || 0;
    const serA22 = h.serA22 || 0;
    const total = common + preseed + seed + serA1 + serA2 + serA22;

    // Common goes to Founders bucket regardless of who holds it
    buckets.Founders += common;
    buckets.Seed += preseed + seed;
    buckets['Series A'] += serA1 + serA2;
    buckets['Series A.2'] += serA22;

    totalExcludingPool += total;
    if (sh.country === 'CAN') canadianShares += total;
  }

  const fullyDiluted = totalExcludingPool + optionPool.total;
  const breakdown = Object.entries(buckets).map(([label, shares]) => ({
    label,
    shares,
    pct: fullyDiluted > 0 ? (shares / fullyDiluted) * 100 : 0,
  })).filter(b => b.shares > 0);

  // Canadian % is calculated excluding the option pool (consistent with
  // SPCC tests and the Equity tab convention).
  const canadianPct = totalExcludingPool > 0 ? (canadianShares / totalExcludingPool) * 100 : 0;

  // Latest round = last round with a price.
  const pricedRounds = rounds.filter(r => r.price);
  const lastRound = pricedRounds[pricedRounds.length - 1];
  const lastRoundName = lastRound?.name || null;
  const lastRoundDate = lastRound?.date || null;
  const lastRoundPrice = lastRound?.price || null;
  const lastRoundPreMoney = lastRoundPrice ? fullyDiluted * lastRoundPrice : null;

  return {
    fullyDiluted,
    breakdown,
    canadianPct: Number(canadianPct.toFixed(2)),
    lastRoundName,
    lastRoundDate,
    lastRoundPreMoney,
    updatedAt,
  };
}
